import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Group, Rect, Text } from 'react-konva'

// Utils & Styles
import baseColors from '../../utils/colors'
import { layoutListLines, previewListLines } from '../../utils/badgeList'
import { rotateAroundCenter } from '../../pages/EventBadgeBuilder/helpers'

/**
 *
 * BadgeList
 *
 * A stack of independently-conditional lines that share one set of type
 * settings. Lines that resolve empty are dropped rather than printed blank, and
 * the lines below them move up — the shift is contained inside this object and
 * never repositions anything else on the badge.
 *
 * Rendered as a Konva `Group` so dragging, transforming and z-ordering come for
 * free, but unlike a badge group it owns its own layout: children are stacked by
 * measured height rather than absolutely positioned.
 *
 * The object grows downward when its content needs more room than
 * `attrs.height` — long lines wrap onto additional lines rather than being
 * truncated or shrunk, so `attrs.height` is a minimum, not a ceiling.
 */
const BadgeList = ({ data, draggable, inGroup, onChange, transformerId, visible }) => {
  // Ref
  const ref = useRef()

  const { attrs } = data

  /**
   * The rows to draw.
   *
   * `resolvedLines` is set by the print path once an attendee is known. Without
   * it we're in the designer, where the authored content is shown as-is with
   * merge-field tokens still visible.
   */
  const layout = useMemo(() => {
    const rows = attrs.resolvedLines || previewListLines(attrs)
    return layoutListLines(attrs, rows)
  }, [
    attrs.resolvedLines,
    attrs.lines,
    attrs.width,
    attrs.fontSize,
    attrs.fontFamily,
    attrs.fontStyle,
    attrs.lineHeight,
    attrs.lineSpacing,
    attrs.uppercase,
    attrs.letterSpacing,
  ])

  // The box never clips its content, so it stretches to whichever is taller.
  const boxHeight = Math.max(attrs.height || 0, layout.height)

  return (
    // `height` is deliberately left as the authored value from the spread
    // rather than the stretched `boxHeight`. Selecting an element copies the
    // Konva node's attrs into the config on the next edit, so writing the
    // stretched height here would ratchet the object's stored minimum height up
    // to whatever its content happened to need.
    <Group
      {...attrs}
      className="List"
      draggable={inGroup ? false : draggable}
      id={attrs.id}
      onDragEnd={(e) => {
        onChange(attrs.id, { x: e.target.x(), y: e.target.y() })
      }}
      onTransformEnd={() => {
        let parent = ref.current.getParent()
        if (inGroup) parent = parent.getParent()
        const transformer = parent.children.find((c) => c.attrs.id === transformerId)

        // The transformer only changes scale, so convert that back into the
        // width and height the object should be stored with.
        const updatedWidth = Math.max(5, transformer.width() * transformer.scaleX())
        const updatedHeight = Math.max(5, transformer.height() * transformer.scaleY())

        // Reset the scales so the re-render draws at the new size rather than a
        // scaled version of the old one.
        _.forEach(transformer.nodes(), (n) => {
          n.scaleX(1)
          n.scaleY(1)
        })

        const rotation = transformer.rotation()
        const updatedPosition = rotateAroundCenter(transformer, rotation)

        onChange(attrs.id, {
          x: updatedPosition.x,
          y: updatedPosition.y,
          rotation,
          width: updatedWidth,
          height: updatedHeight,
        })
      }}
      ref={ref}
      visible={visible}
      width={attrs.width}
      x={attrs.x}
      y={attrs.y}
    >
      {/*
        Background and hit surface for the whole object. Konva ignores the
        actual fill colour when building its hit graph, so a transparent
        background is still clickable — which is what makes the object
        selectable by its empty space, not just by its text.

        The id deliberately avoids the word "base": the panel treats any id
        containing it as the badge background and clears the selection on click.
      */}
      <Rect
        fill={attrs.backgroundFill || 'transparent'}
        height={boxHeight}
        id={`list:${attrs.id}:box`}
        stroke={attrs.stroke}
        strokeWidth={attrs.strokeWidth}
        width={attrs.width}
        x={0}
        y={0}
      />

      {/*
        A list with nothing to draw would otherwise be invisible in the
        designer. `resolvedLines` is only ever set on the print path, so its
        absence is what identifies the designer — the printed badge stays clean.
      */}
      {layout.rows.length === 0 && !attrs.resolvedLines && (
        <Rect
          dash={[4, 4]}
          height={Math.max(attrs.height || 0, 24)}
          listening={false}
          stroke={baseColors.gray[500]}
          strokeWidth={1}
          width={attrs.width}
          x={0}
          y={0}
        />
      )}

      {_.map(layout.rows, (row) => (
        <Text
          align={attrs.align}
          fill={attrs.fill}
          fontFamily={attrs.fontFamily}
          fontSize={parseInt(attrs.fontSize, 10) || 16}
          fontStyle={row.fontStyle}
          key={row.id}
          letterSpacing={Number(attrs.letterSpacing) || 0}
          lineHeight={Number(attrs.lineHeight) || 1.2}
          // Hits belong to the object as a whole — an individual line is not
          // separately selectable, so it must not swallow the click.
          listening={false}
          text={row.text}
          textDecoration={attrs.textDecoration}
          width={attrs.width}
          wrap="word"
          x={0}
          y={row.y}
        />
      ))}
    </Group>
  )
}

BadgeList.defaultProps = {
  draggable: true,
  inGroup: false,
  onChange: () => {},
  visible: true,
}

BadgeList.propTypes = {
  data: PropTypes.object.isRequired,
  draggable: PropTypes.bool,
  inGroup: PropTypes.bool,
  onChange: PropTypes.func,
  transformerId: PropTypes.string.isRequired,
  visible: PropTypes.bool,
}

export default BadgeList
